import { CardItem, CollectionDataLoaded } from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useState,
} from 'react'

type CollectionDataContextType = {
    setCollectionData: (
        contextId: string,
        data: MaybeLoaded<CollectionDataLoaded>,
    ) => void
    getCollectionData: (contextId: string) => MaybeLoaded<CollectionDataLoaded>
}

export const CollectionDataContext = createContext<CollectionDataContextType>({
    setCollectionData: () => {},
    getCollectionData: () => ({ loaded: false }),
})

export type CollectionDataContextProviderProps = {
    contextId: string
}

export const CollectionDataContextProvider = ({
    contextId,
    children,
}: PropsWithChildren<CollectionDataContextProviderProps>) => {
    // PublicationDataContext(s) that wrap this one may be relevant.
    const higherOrderContext = useContext(CollectionDataContext)

    const [storedData, setStoredData] = useState<
        MaybeLoaded<CollectionDataLoaded>
    >({ loaded: false })

    const setPublicationData = useCallback<
        CollectionDataContextType['setCollectionData']
    >(
        (requestedContextId, data) => {
            // If this is the relevant context.
            if (requestedContextId === contextId) {
                setStoredData(data)
            } else {
                // Otherwise keep searching for contexts above this in the DOM tree.
                higherOrderContext.setCollectionData(requestedContextId, data)
            }
        },
        [contextId, higherOrderContext],
    )

    const getPublicationData = useCallback<
        CollectionDataContextType['getCollectionData']
    >(
        (requestedContextId) => {
            // If this is the relevant context.
            if (requestedContextId === contextId) {
                return storedData
            } else {
                // Otherwise keep searching for contexts above this in the DOM tree.
                return higherOrderContext.getCollectionData(requestedContextId)
            }
        },
        [contextId, higherOrderContext, storedData],
    )

    return (
        <CollectionDataContext.Provider
            value={{
                setCollectionData: setPublicationData,
                getCollectionData: getPublicationData,
            }}
        >
            {children}
        </CollectionDataContext.Provider>
    )
}
